




















































































































































































import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import BaseDropDown from '@/components/base/BaseDropDown.vue';
import {
  Site,
  ParticipantFormData,
  CreateProjectParticipantPayload,
  ParticipationLevelEnum,
} from '@/store/modules/projects/types/projects.types';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import { useUserEmail } from '@/utils/user.util';
import { cloneData } from '@/utils/general.util';
import { PacesParticipantRoles } from '@/jbi-shared/types/paces-project.types';

@Component({
  components: {
    BaseDropDown,
    ValidationProvider,
    ValidationObserver,
  },
})
export default class CreateParticipantForm extends Vue {
  @Prop({ default: [] })
  public sites!: Site[];

  @Prop()
  public participant!: CreateProjectParticipantPayload;

  @Prop()
  public isEdit!: boolean;

  @Prop()
  public participantsList!: CreateProjectParticipantPayload[]; // active participants only

  public form: ParticipantFormData = {
    userEmail: '',
    participantRole: null,
    participantSites: [],
  };
  public checkedAll: boolean = false;
  public isRoleProjectCoordinator: boolean = false;
  public selectedSiteNames: string = '';

  public isSameProjectOwnerEmail: boolean = false;
  public isSameParticipantEmail: boolean = false;
  public existingParticipant: CreateProjectParticipantPayload | undefined;
  public showErrorMessage: boolean = false;

  get projectId(): number {
    return +this.$route.params.projectId;
  }

  get isDraft(): boolean {
    return Boolean(this.projectId);
  }

  get roleOptions(): PacesParticipantRoles[] {
    return [
      PacesParticipantRoles.DataCollector,
      PacesParticipantRoles.ProjectCoordinator,
    ];
  }

  get siteOptions(): Site[] {
    if (Array.isArray(this.sites)) {
      return this.sites;
    }
    return [];
  }

  get selectedSiteContent(): string {
    if (this.checkedAll) {
      return 'All Sites';
    }

    if (this.form.participantSites.length === 0) {
      return 'Select Sites';
    }

    const selectedSites: Site[] = this.form.participantSites.filter(
      (participantSite: Site) => {
        return this.siteOptions.map((o) => o.id).includes(participantSite.id);
      },
    );

    this.selectedSiteNames = this.getSelectedSiteNames(selectedSites);
    return this.selectedSiteNames;
  }

  public getSelectedSiteNames(sites: Site[]): string {
    const selectedSitesNames: string = sites
      .map((item: Site) => item.name)
      .join(', ');
    return selectedSitesNames.length > 65
      ? selectedSitesNames.substring(0, 62) + '...'
      : selectedSitesNames;
  }

  getSiteName(id: number): string | null {
    const site = this.sites.find((site) => site.id === id);
    return site ? site.name : null;
  }

  public mounted() {
    if (this.participant) {
      const participant: CreateProjectParticipantPayload = cloneData(
        this.participant,
      );
      const { email, assignedRole, assignedSiteIds } = participant;

      this.form.userEmail = email;
      this.form.participantRole = assignedRole;

      /**
       * form.participantSites must contain EXACT value from
       * siteOption in order for the checkbox to work
       */
      this.form.participantSites = this.transformSites(assignedSiteIds);
    } else {
      // this.form.participantSites = this.siteOptions;
    }
  }

  public transformSites(assignedSiteIds: number[]): Site[] {
    if (assignedSiteIds.length === 0 && this.isRoleProjectCoordinator) {
      return this.siteOptions;
    }

    return this.siteOptions.filter((availableSite: Site) => {
      return assignedSiteIds.map((site) => site).includes(availableSite.id);
    });
  }

  public handleCheckAll(value: boolean): void {
    this.checkedAll = value;
    this.form.participantSites = [];
    if (value) {
      this.form.participantSites = cloneData(this.siteOptions);
    }
  }

  public handleSubmit(): void {
    this.showErrorMessage = false;
    if (this.isSameProjectOwnerEmail || this.checkParticipantExists()) {
      this.showErrorMessage = true;
      return;
    }

    const assignedSiteIds = this.form.participantSites.map(
      (participantSite: Site) => +participantSite.id,
    );

    const formData = {
      projectId: this.projectId,
      email: this.form.userEmail,
      assignedRole: this.form.participantRole,
      assignedSiteIds,
      level: ParticipationLevelEnum.Project,
    };
    // only submit form when email is validated
    this.$emit('submit', formData);
    this.$emit('close');
  }

  get userEmail(): string | undefined {
    return useUserEmail.call(this);
  }

  @Watch('form.participantSites', { deep: true })
  public onCheck(value: Site[]): void {
    this.checkedAll = false;
    if (
      value
        .map(({ id }) => id)
        .sort()
        .toString() ===
      this.siteOptions
        .map(({ id }) => id)
        .sort()
        .toString()
    ) {
      this.checkedAll = true;
    }
  }

  @Watch('form.userEmail')
  public validateDuplication(participantEmail: string): void {
    participantEmail = participantEmail.toLowerCase();

    if (this.isEdit) {
      this.existingParticipant = this.participantsList.find(
        (participant: CreateProjectParticipantPayload) =>
          participant.email.toLowerCase() === participantEmail.toLowerCase() &&
          participant.assignedRole !== this.form.participantRole,
      );
    } else {
      this.existingParticipant = this.participantsList.find(
        (participant: CreateProjectParticipantPayload) =>
          participant.email.toLowerCase() === participantEmail.toLowerCase(),
      );
    }

    switch (participantEmail) {
      case (this.userEmail || '').toLowerCase():
        this.isSameProjectOwnerEmail = true;
        break;
      case this.existingParticipant &&
        this.existingParticipant.email.toLowerCase():
        this.isSameParticipantEmail = true;
        break;
      default:
        this.isSameParticipantEmail = false;
        this.isSameProjectOwnerEmail = false;
        this.showErrorMessage = false;
    }
  }

  private checkParticipantExists(): boolean {
    const existingParticipant:
      | CreateProjectParticipantPayload
      | undefined = this.existingParticipant;

    // If the participant roles are the same then the participant exists
    let isSameRole: boolean = Boolean(
      this.participantsList.find(
        (participant: CreateProjectParticipantPayload) =>
          this.form.userEmail === participant.email &&
          this.form.participantRole === participant.assignedRole,
      ),
    );
    if (this.isEdit && existingParticipant) {
      // for edit it will be enough to match the existing participant role directly
      isSameRole =
        existingParticipant.assignedRole === this.form.participantRole;
    }

    if (isSameRole && existingParticipant) {
      return true;
    }

    // The participant exists if at least one of the sites match with the existing participant
    let isSameSiteExists: boolean = false;
    if (existingParticipant) {
      existingParticipant.assignedSiteIds.some((siteId: number) => {
        isSameSiteExists = Boolean(
          this.form.participantSites.find((site: any) => site.id === siteId),
        );
        return isSameSiteExists;
      });
    }
    return isSameSiteExists;
  }
}
